import React, { useEffect, useState } from "react";
import "./VerifyPhoneNumberv2Popup.scss";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { useTranslation, withTranslation } from "react-i18next";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { actionCreators } from "../../store";
import { RootState } from "../../store/reducers";
import InputMaskField from "../react-input-mask/inputfield";
import { apiService } from "../../services";
import { setCookie } from "../../helper/cookieFunction";
import { handleUserInfo } from "../../helper/customObj";

const VerifyPhoneNumberv2Popup = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {
    setLoginModal,
    setOtpModal,
    setGuestLoggedIn,
    setPendingCheckout,
    setLoggedIn,
    setAuthToken,
    userInfoState,
    freezScreenState,
    alertMessageState,
    langValueState,
  } = bindActionCreators(actionCreators, dispatch);

  const appState = useSelector((state: RootState) => state.appReducer);
  const apiState = useSelector((state: RootState) => state.apiReducer);
  const { otpModal, loginValues, langValue } = appState;
  const storeInfo = apiState.storeInfo;

  const [OTP, setOTP] = useState("");
  const [loading, setLoading] = useState(false);
  const [sendAgain, setSendAgain] = useState(false);
  const [timer, setTimer] = useState(60);
  const [timerKey, setTimerKey] = useState(0);

  useEffect(() => {
    if (!otpModal) return;
    setOTP("");
    setSendAgain(false);
    setTimerKey((k) => k + 1);
  }, [otpModal]);

  useEffect(() => {
    if (!otpModal || sendAgain) return;
    let timeleft = 59;
    setTimer(timeleft);
    const downloadTimer = setInterval(() => {
      if (timeleft <= 0) {
        clearInterval(downloadTimer);
        setSendAgain(true);
      } else {
        setTimer(timeleft);
        timeleft -= 1;
      }
    }, 1000);
    return () => clearInterval(downloadTimer);
  }, [otpModal, timerKey, sendAgain]);

  useEffect(() => {
    const handleCheckOtp = async () => {
      setLoading(true);
      freezScreenState(true);
      try {
        const response: any = await apiService.checkGuestOtp({
          OTP,
          phoneNumber: loginValues.phoneNumber,
          countryCode: loginValues.phoneCode,
          firstName: loginValues.fullName || null,
          provider: loginValues.provider || storeInfo?.otpProvider || null,
        });

        if (response?.data?.success === true && response?.data?.data != false) {
          const token = response.data.data.token;
          const userInfo = handleUserInfo({
            ...response.data.data,
            access_token: token,
            name: response.data.data.name || response.data.data.first_name || loginValues.fullName,
          });
          setCookie("tokenAuth", token, 365);
          setCookie("Profile", JSON.stringify(userInfo), 365);
          sessionStorage.setItem("token", token);
          setAuthToken(token);
          userInfoState(userInfo);
          setLoggedIn(token);
          setGuestLoggedIn(false);
          setOTP("");
          alertMessageState({
            type: "success",
            show: true,
            message: response.data.message || t("Login.Verified-successfully"),
          });
          setOtpModal(false);
          setLoginModal(false);
          setPendingCheckout(true);
        } else {
          alertMessageState({
            type: "error",
            show: true,
            message: response?.data?.message || t("Login.Invalid-OTP"),
          });
        }
      } catch (e: any) {
        alertMessageState({
          type: "error",
          show: true,
          message: e?.message || t("Login.check-network"),
        });
      } finally {
        setLoading(false);
        freezScreenState(false);
      }
    };

    if (OTP.length === 5) {
      handleCheckOtp();
    }
  }, [OTP]);

  if (!otpModal) return null;

  const isRtl = langValue === "ar";

  const toggleLang = () => {
    const next = isRtl ? "en" : "ar";
    langValueState(next);
    i18n.changeLanguage(next);
  };

  const handleClose = () => {
    setOtpModal(false);
    setLoginModal(false);
    setPendingCheckout(false);
  };

  const handleChangeNumber = () => {
    setOtpModal(false);
    setLoginModal(true);
    setOTP("");
  };

  const handleSendOtp = async () => {
    setLoading(true);
    freezScreenState(true);
    try {
      const response: any = await apiService.guestOtp(
        loginValues.phoneCode,
        loginValues.phoneNumber,
        {
          store_id: loginValues.storeId || storeInfo?.storeId || null,
          branch_id: loginValues.branchId || storeInfo?.branchId || null,
          provider: loginValues.provider || storeInfo?.otpProvider || null,
        }
      );
      if (response?.data?.success === true || response?.data?.data === true) {
        alertMessageState({
          type: "success",
          show: true,
          message: response.data.message || t("Login.OTP-sent"),
        });
      } else {
        alertMessageState({
          type: "error",
          show: true,
          message: response?.data?.message || t("Login.Failed-to-send-OTP"),
        });
      }
      setSendAgain(false);
      setTimerKey((k) => k + 1);
    } catch (e: any) {
      alertMessageState({
        type: "error",
        show: true,
        message: e?.message || t("Login.check-network"),
      });
      setSendAgain(false);
      setTimerKey((k) => k + 1);
    } finally {
      setLoading(false);
      freezScreenState(false);
    }
  };

  const handlechangeSetOtp = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 5) setOTP(value);
  };

  return (
    <Modal
      open
      onClose={handleClose}
      disableAutoFocus={false}
      disableEnforceFocus
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.45)",
        },
      }}
      sx={{ zIndex: 140000 }}
    >
      <div
        className={`otp-modal-sheet ${isRtl ? "otp-modal-sheet--rtl direction-rtl" : "direction-ltr"}`}
        dir={isRtl ? "rtl" : "ltr"}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {loading && (
          <Backdrop
            open
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              borderRadius: "20px 20px 0 0",
              color: "#fff",
            }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        <div className="otp-modal-sheet__header">
          <button type="button" className="otp-modal-sheet__back" onClick={handleChangeNumber} aria-label={t("Login.Back")}>
            {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
          </button>
          <button type="button" className="otp-modal-sheet__lang" onClick={toggleLang}>
            <span className="otp-modal-sheet__lang-code">{isRtl ? "EN" : "ع"}</span>
            <span className="otp-modal-sheet__lang-label">{isRtl ? "English" : "العربية"}</span>
          </button>
        </div>
        <h1 className="otp-modal-sheet__title familysora700">{t("Login.Verify-number")}</h1>
        <div className="otp-modal-sheet__hint familysora400">
          <span>{t("Login.Enter-the-code-sent-to")}</span>
          <span className="otp-modal-sheet__phone">
            +{loginValues?.phoneCode} {loginValues?.phoneNumber}
          </span>
        </div>
        <div className="otp-modal-sheet__otp" dir="ltr">
          <InputMaskField
            value={OTP}
            onChange={handlechangeSetOtp}
            inputProps={{ maxLength: 5, inputMode: "numeric", autoComplete: "one-time-code" }}
            hideLabel
            label={null}
            variant="outlined"
            type="tel"
            mask="99999"
            name="otp"
            autoFocus
            autoComplete="off"
          />
        </div>
        <div className="otp-modal-sheet__row">
          <span className="otp-modal-sheet__muted">{t("Login.Did-not-receive")}?</span>
          {sendAgain ? (
            <span className="otp-modal-sheet__action" onClick={handleSendOtp}>
              {t("Login.Send-again")}
            </span>
          ) : (
            <span className="otp-modal-sheet__muted">({timer} sec)</span>
          )}
        </div>
        <div className="otp-modal-sheet__row">
          <span className="otp-modal-sheet__action" onClick={handleChangeNumber}>
            {t("Login.Different-number")}
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default withTranslation()(VerifyPhoneNumberv2Popup);
